import { z } from 'zod'
import { timestamp } from "../util"
import { RComments } from "./Comment"
import { DrugIcd } from '@fusion-advantage/backcompat'

// The difference between "source" and "evidence" on a DiagnosisItem—
// * Sources supplement the evidence on the Form as the reason for why a
//   particular Dx is present on the Assessment table.
//   "It's in the patient history", or
//   "I as a clinician added it manually".
//   If it's just because of dxEvidence or rxEvidence on the Form, a source is
//   not required.
// * Sources are mutually exclusive (cannot be both history AND adhoc).
// * Clinicians can jump to evidence on the form and change it.
// * Sources are non-interactive, and cannot be set or changed by the
//   clinician, but the clinician can create new adhoc Diagnoses
//   (when the feature is enabled).
// * For adhoc diagnoses, the clinician is required to enter a
//   comment, which will be "comment evidence" and show up as its own
//   evidence icon.
export enum DiagnosisSource {
	DxHistory = 'History',
	Adhoc = 'Adhoc',
}

export const RDiagnosisSource = z.enum(DiagnosisSource)

export enum AssertionStatus {
	UnassertedByQuery = 'UNASSERTED_BY_QUERY',
	// 20251229 New customer VNS' auditors did not like the word "ASSERTED"
	// for HEDIS purposes, especially on items with a claims history source
	// (DiagnosisSource.DxHistory), and requested we change it to "ACTIVE".
	// Dan argues this is semantics and has suggested we do not change the
	// `Asserted` key itself here but just its value.
	Asserted = 'ACTIVE',
	HistoryOf = 'HISTORY_OF',
	Indeterminate = 'INDETERMINATE',
}
export const RAssertionStatus = z.enum(AssertionStatus)

export const getStatusDisplayValue = (assertionStatus: AssertionStatus|undefined|null): string => {
	switch(assertionStatus) {
		case AssertionStatus.UnassertedByQuery:
			return 'Unasserted By Query'
		case AssertionStatus.Asserted:
			return 'Active'
		case AssertionStatus.HistoryOf:
			return 'History of'
		case AssertionStatus.Indeterminate:
			return 'Indeterminate'
	}
	return ''
}

export enum DiagnosisPlan {
	Continue = 'CONTINUE',
	PCP = 'PCP',
	Urgent = 'URGENT',
}
export const RDiagnosisPlan = z.enum(DiagnosisPlan)

export const getCarePlanDisplayValue = (carePlan: DiagnosisPlan|undefined|null): string => {
	switch(carePlan) {
		case DiagnosisPlan.Continue:
			return 'Continue'
		case DiagnosisPlan.PCP:
			return 'PCP'
		case DiagnosisPlan.Urgent:
			return 'Urgent'
	}
	return ''
}

export enum AuditDecision {
	Accepted = 'ACCEPTED',
	Question = 'QUESTION',
	Rejected = 'REJECTED',
}
export const RAuditDecision = z.enum(AuditDecision)

export const RDiagnosisReviewItem = z.object({
	cid: z.union([z.string(), z.null()]),
	icd10Code: z.string(),
	icdComorbidityStatus: z.nullable(z.string()),
	title: z.string(),
	source: z.union([RDiagnosisSource, z.null()]),
	status: z.union([RAssertionStatus, z.null()]),
	dxOnAssessment: z.boolean(),
	dxEvidence: z.boolean(),
	rxEvidence: z.boolean(),
	comments: RComments,
	carePlan: z.union([RDiagnosisPlan, z.null()]),
	auditDecision: z.union([RAuditDecision, z.null()]),
	// requested by Denise Sassaki for reporting;
	// use sqlServerDateTimeOffset
	auditDecisionUpdatedAt: z.optional(z.string()),
	auditComments: RComments,
	// temporary storage (between drafts, not submissions)
	// of the state of the item when last submitted
	prevSubmission: z.optional(z.object({
		auditDecision: z.union([RAuditDecision, z.null()]),
		auditDecisionUpdatedAt: z.optional(z.string()),
	})),
})

export type DiagnosisReviewItem = z.infer<typeof RDiagnosisReviewItem>

export const newDiagnosisReviewItem = (cid: string|null, icd10Code: string): DiagnosisReviewItem => {
	return {
		cid,
		icd10Code,
		icdComorbidityStatus: null,
		status: null,
		comments: [],
		carePlan: null,
		title: '',
		source: null,
		dxOnAssessment: false,
		dxEvidence: false,
		rxEvidence: false,
		auditDecision: null,
		auditComments: [],
	}
}

// after an item has been updated,
// this function considers the update that has been made to the item
// and can make additional changes accordingly
export const diagnosisReviewItemPostUpdate = (prevItem: DiagnosisReviewItem, nextItem: DiagnosisReviewItem, drugIcdsByCode: Record<string, DrugIcd>): DiagnosisReviewItem => {
	if(nextItem.icd10Code in drugIcdsByCode) {
		nextItem.icdComorbidityStatus = drugIcdsByCode[nextItem.icd10Code].ComorbidityStatus
	}
	switch(diagnosisReviewItemPresence(nextItem)) {
		case DiagnosisReviewItemPresence.ForcedAsserted:
			nextItem.status = AssertionStatus.Asserted
			break
		case DiagnosisReviewItemPresence.UnassertedByQuery:
			// allow an item to not be asserted, but remain on the
			// list so its audit comments can still be viewed
			nextItem.status = AssertionStatus.UnassertedByQuery
			break
	}
	if(prevItem.status !== nextItem.status) {
		// [ISSUE-603] "auto select Continue if Dx is Asserted"
		// There's nothing in the spec about the care plan being forced,
		// so for now we'll just be gentle about it
		if(nextItem.status === AssertionStatus.Asserted && !nextItem.carePlan) {
			nextItem.carePlan = DiagnosisPlan.Continue
		}
	}
	// [ISSUE-699] "Assessment table: Un-set a forced Asserted status once
	// no longer forced"
	// 20250129 cblack — Of the three approaches outlined in the ticket for
	// handling the request, Dan and Naomi asked to move forward with:
	// "Any diagnosis sourced from claims history, which has its Form-based
	//  or Indication evidence removed by the clinician, and does not have its
	//  status forced for any other reason (query, etc.), should have its
	//  status be un-set."
	// 20250204 - Thad's implementation notes:
	// Per ticket 699 when clinician adds form evidence associated with claim
	// history and then removes it, the status of the diagnosis review item
	// should be set back to null
	// (as initially set for claims history dx items)
	// per current code, dont need to check item presence here as any item with
	// dxHistory will result in Present status if no dx or rx evidence is
	// selected
	if(nextItem.source === DiagnosisSource.DxHistory &&
		diagnosisReviewItemHasEvidence(prevItem) === true &&
		diagnosisReviewItemHasEvidence(nextItem) === false) {
		nextItem.status = null
	}

	if(!diagnosisReviewItemRequiresCarePlan(nextItem)) {
		nextItem.carePlan = null
	}
	// 20240620 don't change the auditDecision while the clinician
	// is editing; the review button can continue to display even if
	// they unassert, so they can finish their responses to all
	// queries.  instead, we will change this in formFinalizeForSubmit.
	// if(!diagnosisReviewItemRequiresAuditDecision(item)) {
	// 	item.auditDecision = null
	// }
	if(nextItem.auditDecision !== prevItem.auditDecision) {
		// if the decision reverts to what was previously submitted
		if(nextItem.auditDecision === prevItem.prevSubmission?.auditDecision) {
			nextItem.auditDecisionUpdatedAt = prevItem.prevSubmission.auditDecisionUpdatedAt
		} else {
			nextItem.auditDecisionUpdatedAt = timestamp()
		}
	}
	return nextItem
}

export const diagnosisReviewItemRequiresCarePlan = (item: DiagnosisReviewItem) : boolean => {
	return item.status === AssertionStatus.Asserted
}

// these happen sometimes in unexpected corner cases.
// we need to allow auditors to query these lines back to the clinician
// so that they can fill in the missing information
export const diagnosisReviewItemMissingClinicalInfo = (item: DiagnosisReviewItem): boolean => {
	if(!item.status) {
		return true
	}
	if(diagnosisReviewItemRequiresCarePlan(item) && !item.carePlan) {
		return true
	}
	return false
}

export const diagnosisReviewItemPendingClinicalInfoQuery = (item: DiagnosisReviewItem): boolean => {
	return diagnosisReviewItemMissingClinicalInfo(item) && item.auditDecision !== AuditDecision.Question
}

export const diagnosisReviewItemRequiresAuditDecision = (item: DiagnosisReviewItem): boolean => {
	return item.status === AssertionStatus.Asserted
}

export const diagnosisReviewItemShouldCleanupAuditDecision = (item: DiagnosisReviewItem): boolean => {
	return !diagnosisReviewItemRequiresAuditDecision(item) && item.auditDecision !== AuditDecision.Question
}

export const diagnosisReviewItemIsCodable = (item: DiagnosisReviewItem): boolean => {
	return item.status === AssertionStatus.Asserted && item.auditDecision === AuditDecision.Accepted
}

export const diagnosisReviewItemHasEvidence = (item: DiagnosisReviewItem): boolean => {
	return (item.dxEvidence || item.rxEvidence)
}

export enum DiagnosisReviewItemPresence {
	ForcedAsserted,
	Present,
	UnassertedByQuery,
	Drop,
}

export const diagnosisReviewItemPresence = (item: DiagnosisReviewItem): DiagnosisReviewItemPresence => {
	// [ISSUE-596] force status Asserted if dx evidence
	// [ISSUE-506] force status Asserted if rx evidence
	if(diagnosisReviewItemHasEvidence(item) || item.source === DiagnosisSource.Adhoc) {
		return DiagnosisReviewItemPresence.ForcedAsserted
	}
	if(item.source === DiagnosisSource.DxHistory) {
		return DiagnosisReviewItemPresence.Present
	}
	if(item.auditComments.length > 0) {
		return DiagnosisReviewItemPresence.UnassertedByQuery
	}
	return DiagnosisReviewItemPresence.Drop
}

export const diagnosisReviewItemCanTrash = (item: DiagnosisReviewItem): boolean => {
	return (item.source === DiagnosisSource.Adhoc && item.auditComments.length < 1)
}

export const diagnosisReviewItemIsPendingAuditComment = (item: DiagnosisReviewItem, encounterAppLastStatusDT: string|undefined): boolean => {
	switch(item.auditDecision) {
		case AuditDecision.Question:
			break
		case AuditDecision.Rejected:
			if(item.auditDecision === item.prevSubmission?.auditDecision) {
				// no change since form last submitted, not required
				return false
			}
			break
		default:
			// not required
			return false
	}
	if(!encounterAppLastStatusDT) {
		// do not pass, this value should be present
		return true
	}
	const lastAuditComment = item.auditComments.at(-1)
	if(lastAuditComment && lastAuditComment.encounterAppLastStatusDT === encounterAppLastStatusDT) {
		return false
	}
	return true
}

export const diagnosisReviewItemDefaultAuditComment = (item: DiagnosisReviewItem): string|undefined => {
	if(diagnosisReviewItemMissingClinicalInfo(item) && item.auditDecision === AuditDecision.Question) {
		return 'Please review the status and care plan on this diagnosis.'
	}
}

export const diagnosisReviewItemShouldShowCommentsToClinician = (item: DiagnosisReviewItem): boolean => {
	return item.auditDecision === AuditDecision.Question
}
